import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge } from 'react-bootstrap';
import { BsStarFill, BsHouseDoor, BsTrophy, BsClock } from 'react-icons/bs';
import './AgentMatch.css';

interface Agent {
  id: string;
  name: string;
  photo: string;
  rating: number;
  reviews: number;
  specialties: string[];
  soldProperties: number;
  yearsExperience: number;
  avgDaysToSell: number;
  description: string;
  matchScore: number;
}

const AgentMatch: React.FC = () => {
  const [step, setStep] = useState(1);
  const [preferences, setPreferences] = useState({
    transactionType: 'buy',
    propertyType: 'residential',
    priceRange: '',
    timeline: '',
    importantFactors: [] as string[]
  });
  const [matchedAgents, setMatchedAgents] = useState<Agent[]>([]);

  const handleFactorToggle = (factor: string) => {
    setPreferences(prev => ({
      ...prev,
      importantFactors: prev.importantFactors.includes(factor)
        ? prev.importantFactors.filter(f => f !== factor)
        : [...prev.importantFactors, factor]
    }));
  };

  const handleFindAgents = () => {
    // TODO: Replace with actual API call
    const mockAgents: Agent[] = [
      {
        id: '1',
        name: 'Sarah Johnson',
        photo: '/api/placeholder/100/100',
        rating: 4.9,
        reviews: 127,
        specialties: ['First-time buyers', 'Luxury homes', 'Negotiation expert'],
        soldProperties: 89,
        yearsExperience: 12,
        avgDaysToSell: 24,
        description: 'Top-rated agent specializing in helping first-time buyers navigate the market.',
        matchScore: 98
      },
      {
        id: '2',
        name: 'Michael Chen',
        photo: '/api/placeholder/100/100',
        rating: 4.8,
        reviews: 98,
        specialties: ['Investment properties', 'Market analysis', 'Fast closings'],
        soldProperties: 156,
        yearsExperience: 15,
        avgDaysToSell: 21,
        description: 'Investment specialist with deep market knowledge and proven track record.',
        matchScore: 94
      },
      {
        id: '3',
        name: 'Jennifer Martinez',
        photo: '/api/placeholder/100/100',
        rating: 4.7,
        reviews: 82,
        specialties: ['Relocation specialist', 'Family homes', 'School districts'],
        soldProperties: 67,
        yearsExperience: 8,
        avgDaysToSell: 28,
        description: 'Family-focused agent who knows every school district in the valley.',
        matchScore: 91
      }
    ];

    setMatchedAgents(mockAgents);
    setStep(2);
  };

  const renderStep1 = () => (
    <Card className="agent-match-card">
      <Card.Body>
        <h2>Find Your Perfect Agent Match</h2>
        <p>Our AI matches you with agents based on your specific needs</p>

        <Form>
          <Form.Group className="mb-4">
            <Form.Label>Are you buying or selling?</Form.Label>
            <div className="button-group">
              <Button
                variant={preferences.transactionType === 'buy' ? 'primary' : 'outline-primary'}
                onClick={() => setPreferences({ ...preferences, transactionType: 'buy' })}
              >
                I'm Buying
              </Button>
              <Button
                variant={preferences.transactionType === 'sell' ? 'primary' : 'outline-primary'}
                onClick={() => setPreferences({ ...preferences, transactionType: 'sell' })}
              >
                I'm Selling
              </Button>
              <Button
                variant={preferences.transactionType === 'both' ? 'primary' : 'outline-primary'}
                onClick={() => setPreferences({ ...preferences, transactionType: 'both' })}
              >
                Both
              </Button>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Property Type</Form.Label>
            <Form.Select
              value={preferences.propertyType}
              onChange={(e) => setPreferences({ ...preferences, propertyType: e.target.value })}
            >
              <option value="residential">Residential</option>
              <option value="condo">Condo/Townhouse</option>
              <option value="luxury">Luxury</option>
              <option value="investment">Investment</option>
              <option value="commercial">Commercial</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Price Range</Form.Label>
            <Form.Select
              value={preferences.priceRange}
              onChange={(e) => setPreferences({ ...preferences, priceRange: e.target.value })}
            >
              <option value="">Select price range</option>
              <option value="0-300k">Under $300k</option>
              <option value="300-500k">$300k - $500k</option>
              <option value="500-750k">$500k - $750k</option>
              <option value="750k-1m">$750k - $1M</option>
              <option value="1m+">Over $1M</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Timeline</Form.Label>
            <Form.Select
              value={preferences.timeline}
              onChange={(e) => setPreferences({ ...preferences, timeline: e.target.value })}
            >
              <option value="">Select timeline</option>
              <option value="asap">ASAP</option>
              <option value="1-3months">1-3 months</option>
              <option value="3-6months">3-6 months</option>
              <option value="6months+">6+ months</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>What's most important to you?</Form.Label>
            <div className="factor-buttons">
              {[
                'Local expertise',
                'Fast response time',
                'Negotiation skills',
                'First-time buyer friendly',
                'Technology savvy',
                'Great reviews'
              ].map(factor => (
                <Button
                  key={factor}
                  variant={preferences.importantFactors.includes(factor) ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleFactorToggle(factor)}
                  className="factor-btn"
                >
                  {factor}
                </Button>
              ))}
            </div>
          </Form.Group>

          <Button 
            variant="primary" 
            size="lg" 
            className="w-100"
            onClick={handleFindAgents}
          >
            Find My Agent Match
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderStep2 = () => (
    <>
      <h2 className="text-center mb-4">Your Top Agent Matches</h2>
      <Row>
        {matchedAgents.map((agent) => (
          <Col key={agent.id} lg={12} className="mb-4">
            <Card className="agent-card">
              <Card.Body>
                <Row>
                  <Col md={3} className="text-center">
                    <img src={agent.photo} alt={agent.name} className="agent-photo" />
                    <h4 className="mt-2">{agent.name}</h4>
                    <div className="rating">
                      <BsStarFill color="#fbbf24" />
                      <span className="ms-1">{agent.rating} ({agent.reviews} reviews)</span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <Badge bg="success" className="match-badge mb-3">
                      {agent.matchScore}% Match
                    </Badge>
                    <p className="agent-description">{agent.description}</p>
                    <div className="specialties">
                      {agent.specialties.map((specialty, idx) => (
                        <Badge key={idx} bg="secondary" className="me-2 mb-2">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="agent-stats">
                      <div className="stat">
                        <BsHouseDoor />
                        <span>{agent.soldProperties} homes sold</span>
                      </div>
                      <div className="stat">
                        <BsTrophy />
                        <span>{agent.yearsExperience} years experience</span>
                      </div>
                      <div className="stat">
                        <BsClock />
                        <span>{agent.avgDaysToSell} avg days to sell</span>
                      </div>
                    </div>
                    <Button variant="primary" className="w-100 mt-3">
                      Contact Agent
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      
      <div className="text-center mt-4">
        <Button variant="outline-primary" onClick={() => setStep(1)}>
          Adjust Preferences
        </Button>
      </div>
    </>
  );

  return (
    <div className="agent-match-page">
      <nav className="chat-nav">
        <Container>
          <div className="nav-content">
            <div className="brand">
              <h1>Chat.RealEstate</h1>
            </div>
            <div className="nav-links">
              <a href="/">Home</a>
              <a href="/chat">Property Search</a>
              <a href="/home-value">Home Value</a>
              <a href="/sign-in">Sign In</a>
            </div>
          </div>
        </Container>
      </nav>

      <Container className="agent-match-container">
        <Row className="justify-content-center">
          <Col lg={step === 1 ? 6 : 10}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AgentMatch;